import { Permission } from "domain/entities/permission";
import { UserInputFilter } from "domain/models/user-input-filter";
import { translateText } from "infra/translation/translate";
import Spinner from "presentation/components/Shared/Spinner";
import {
  useDeletePermissionMutation,
  usePermissionByFilter,
} from "presentation/hooks/use-query-permission";
import { useFilterPermissionStore } from "presentation/stores/use-filter-permission-store";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import HeaderPermissionGrid from "./HeaderPermissionGrid";
import PermissionTypeDropdown from "./PermissionTypeDropdown";
import PermissionTypeSwitch from "./PermissionTypeSwitch";
import PermissionForm from "./PermissionForm";

/**
 * Componente principal para a gestão de permissões do sistema.
 *
 * Responsabilidades:
 * - Exibir uma lista paginada e filtrável de permissões utilizando um `DataTable`.
 * - Gerir o estado de paginação, ordenação e filtros (lazy loading).
 * - Controlar a abertura e fecho de um modal para criação e edição de permissões.
 * - Orquestrar as ações de adicionar, editar e apagar permissões, incluindo diálogos de confirmação.
 *
 * @returns {React.FC} O componente de gestão de permissões.
 */
const PermissionManagement: React.FC = () => {
  const [currentPermission, setCurrentPermission] = useState<Permission>();
  const { filters, setFilters } = useFilterPermissionStore();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "role.name",
    sortOrder: "asc",
    limit: 50,
    page: 1,
    filters: {},
  });

  // Estado centralizado para o modal de criação/edição
  const [showModal, setShowModal] = useState(false);
  const { mutate: deletePermission, isPending: isDeleting } =
    useDeletePermissionMutation();

  const {
    data: permissions,
    isLoading,
    isError,
    error,
    refetch,
  } = usePermissionByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as UserInputFilter);

  /**
   * Efeito que processa e atualiza os filtros no estado `lazyParams`
   * sempre que os filtros brutos do Zustand store são alterados.
   */
  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  /**
   * Efeito que dispara a busca de dados (`refetch`) sempre que qualquer
   * parâmetro de `lazyParams` (filtros, paginação, ordenação) é modificado.
   */
  useEffect(() => {
    refetch();
  }, [lazyParams, refetch]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  /**
   * Abre o modal de edição e define a permissão que será editada.
   * @param {Permission} permission - O objeto de permissão a ser editado.
   */
  const onEdit = (permission: Permission) => {
    setCurrentPermission(permission);
    setShowModal(true);
  };

  /**
   * Abre o modal em modo de criação, garantindo que não há nenhuma permissão selecionada.
   */
  const onAdd = () => {
    setCurrentPermission(undefined); // Garante que o formulário estará vazio
    setShowModal(true);
  };

  /**
   * Inicia o processo de exclusão de uma permissão.
   * Exibe um diálogo de confirmação antes de chamar a mutação de exclusão.
   * @param {string} id - O ID da permissão a ser apagada.
   * @param {string} name - O nome da permissão (usado na mensagem de confirmação).
   */
  const handleDeleteRequest = (id: string, name: string) => {
    confirmDialog({
      message: `Tem a certeza que deseja apagar a permissão para "${name}"?`,
      header: "Confirmação de Exclusão",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: () =>
        deletePermission(id, {
          onSuccess: () => handleDeleteSuccess(name),
          onError: (error) => handleDeleteError(error, name),
        }),
    });
  };

  /**
   * Manipulador de erro para a exclusão de permissão. Exibe uma notificação de erro.
   * @param {any} error - O objeto de erro retornado pela mutação.
   * @param {string} name - O nome da permissão que falhou ao ser apagada.
   */
  const handleDeleteError = (error: any, name: string) => {
    const message = `Falha ao apagar a permissão para "${name}".`;
    toast.current?.show({
      severity: "error",
      summary: "Erro",
      detail: message,
      life: 5000,
    });
  };

  /**
   * Manipulador de sucesso para a exclusão de permissão.
   * Dispara um `refetch` para atualizar a lista e exibe uma notificação de sucesso.
   * @param {string} name - O nome da permissão que foi apagada.
   */
  const handleDeleteSuccess = (name: string) => {
    refetch();
    toast.current?.show({
      severity: "success",
      summary: "Sucesso",
      detail: `Permissão para "${name}" apagada com sucesso.`,
      life: 5000,
    });
  };

  /**
   * Fecha o modal de criação/edição e limpa o estado `currentPermission`.
   */
  const handleCloseModal = () => {
    setShowModal(false);
    // Limpa a permissão atual para garantir que o formulário de "criar" esteja sempre limpo
    setCurrentPermission(undefined);
  };

  /**
   * Renderiza os ícones de ação (editar, apagar) para cada linha da tabela.
   * @param {Permission} rowData - Os dados da linha atual.
   */
  const showActionOptions = (rowData: Permission) => {
    if (!rowData) return null;

    return (
      <div className="spacing-icons">
        <i
          title="Editar permissão"
          className="pi pi-pencil cursor-pointer"
          style={{ color: "var(--primary-color)" }}
          onClick={() => onEdit(rowData)}
        />
        <i
          title="Excluir permissão"
          className="pi pi-times cursor-pointer"
          style={{ color: "var(--primary-color)" }}
          onClick={() =>
            handleDeleteRequest(rowData.id!, rowData.function?.label || "")
          }
        />
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={permissions?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={permissions?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderPermissionGrid onAdd={onAdd} />}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
      >
        <Column
          field="role.name"
          header="Perfil"
          sortable
          sortField="role.name"
          filter
          showFilterMatchModes={false}
          filterField="queryRole"
        />
        <Column
          field="function.label"
          header="Função"
          sortable
          sortField="function.label"
          filter
          showFilterMatchModes={false}
          filterField="queryFunction"
        />
        <Column
          field="allowSave"
          header="Salvar"
          sortable
          dataType="boolean"
          filter
          filterElement={(options) => (
            <PermissionTypeDropdown options={options} />
          )}
          showFilterMatchModes={false}
          filterField="allowSave"
          body={(rowData: Permission) => (
            <PermissionTypeSwitch
              permissionId={rowData.id}
              value={rowData?.allowSave}
            />
          )}
        />
        <Column
          field="allowEdit"
          header="Editar"
          sortable
          dataType="boolean"
          filter
          filterElement={(options) => (
            <PermissionTypeDropdown options={options} />
          )}
          showFilterMatchModes={false}
          filterField="allowEdit"
          body={(rowData: Permission) => (
            <PermissionTypeSwitch
              permissionId={rowData.id}
              value={rowData?.allowEdit}
            />
          )}
        />
        <Column
          field="allowDelete"
          header="Excluir"
          sortable
          dataType="boolean"
          filter
          filterElement={(options) => (
            <PermissionTypeDropdown options={options} />
          )}
          showFilterMatchModes={false}
          filterField="allowDelete"
          body={(rowData: Permission) => (
            <PermissionTypeSwitch
              permissionId={rowData.id}
              value={rowData?.allowDelete}
            />
          )}
        />
        <Column
          field="allowView"
          header="Exibir"
          sortable
          dataType="boolean"
          filter
          filterElement={(options) => (
            <PermissionTypeDropdown options={options} />
          )}
          showFilterMatchModes={false}
          filterField="allowView"
          body={(rowData: Permission) => (
            <PermissionTypeSwitch
              permissionId={rowData.id}
              value={rowData?.allowView}
            />
          )}
        />
        <Column
          body={showActionOptions}
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Dialog
        header="Configurações da Permissão"
        visible={showModal}
        style={{ width: "40vw" }}
        onHide={handleCloseModal}
      >
        <PermissionForm
          currentPermission={currentPermission}
          onSuccess={handleCloseModal}
          toast={toast}
        />
      </Dialog>
      {/* Adiciona o componente Toast para que as mensagens possam ser exibidas */}
      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog />
    </div>
  );
};

export default PermissionManagement;
